/**
 * ESS POS — Centralized API Client
 *
 * Enforces:
 * - Configurable API base URL (never hardcoded to one server IP)
 * - JWT Bearer Authentication & automatic /auth/refresh rotation
 * - Tenant isolation (never trusts URL overrides; clears cache on logout/tenant switch)
 * - Request timeouts & AbortController cancellation
 * - Request IDs for distributed tracing
 * - Safe retry policy ONLY for idempotent GET requests (never retries payments or sales)
 */

import {
  AuthenticatedUser,
  AuthTokens,
  BranchRecord,
  CashierShiftRecord,
  CategoryRecord,
  DailySalesReport,
  InventoryBalanceRecord,
  InventoryMovementRecord,
  InventoryValuationReport,
  LoginResponseData,
  ProductRecord,
  SaleRecord,
  StockLocationRecord,
  SubscriptionStatusResponse,
  TenantSummary,
  WorkspaceId,
  QrOrderSubmissionRecord,
  ActiveBillRecord,
} from '../types/contracts';

declare global {
  interface Window {
    __ESS_API_BASE_URL__?: string;
  }
}

export class ApiError extends Error {
  public statusCode: number;
  public errorCode: string;
  public details?: Array<{ path: string; message: string }>;
  public subscriptionStatus?: string;
  public renewalUrl?: string;

  constructor(params: {
    message: string;
    statusCode: number;
    errorCode: string;
    details?: Array<{ path: string; message: string }>;
    subscriptionStatus?: string;
    renewalUrl?: string;
  }) {
    let finalMessage = params.message;
    if (params.details && Array.isArray(params.details) && params.details.length > 0) {
      const detailStr = params.details
        .map((d) => (d.path ? `${d.path}: ${d.message}` : d.message))
        .join('; ');
      finalMessage = `${params.message} (${detailStr})`;
    }
    super(finalMessage);
    this.name = 'ApiError';
    this.statusCode = params.statusCode;
    this.errorCode = params.errorCode;
    this.details = params.details;
    this.subscriptionStatus = params.subscriptionStatus;
    this.renewalUrl = params.renewalUrl;
  }
}

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const SESSION_STORAGE_KEY = 'ess_pos_active_session_v1';

class EssApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private currentUser: AuthenticatedUser | null = null;
  private currentTenant: TenantSummary | null = null;
  private activeWorkspace: WorkspaceId | null = null;
  private superAdminTargetTenantId: string | null = null;

  // Controlled client-side cache isolated by userId + tenantId
  private cache = new Map<string, CacheEntry<any>>();
  private activeControllers = new Map<string, AbortController>();
  private onUnauthorizedCallback: (() => void) | null = null;

  constructor() {
    this.baseUrl = window.__ESS_API_BASE_URL__ || '/api/v1';
    this.restoreSession();
  }

  public setBaseUrl(url: string) {
    this.baseUrl = url.replace(/\/+$/, '');
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }

  public onUnauthorized(cb: () => void) {
    this.onUnauthorizedCallback = cb;
  }

  private restoreSession() {
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.accessToken && parsed.user) {
        this.accessToken = parsed.accessToken;
        this.refreshToken = parsed.refreshToken || null;
        this.currentUser = parsed.user;
        this.currentTenant = parsed.tenant || null;
        this.activeWorkspace = parsed.activeWorkspace || this.resolvePrimaryWorkspace(parsed.user);
      }
    } catch {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }

  private persistSession() {
    try {
      if (!this.accessToken || !this.currentUser) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({
          accessToken: this.accessToken,
          refreshToken: this.refreshToken,
          user: this.currentUser,
          tenant: this.currentTenant,
          activeWorkspace: this.activeWorkspace,
        })
      );
    } catch {
      // Ignore storage quota errors
    }
  }

  public getUser(): AuthenticatedUser | null {
    return this.currentUser;
  }

  public getTenant(): TenantSummary | null {
    return this.currentTenant;
  }

  public updateTenantStatusLocal(status: TenantSummary['status']) {
    if (this.currentTenant) {
      this.currentTenant.status = status;
      this.persistSession();
    }
  }

  public isAuthenticated(): boolean {
    return Boolean(this.accessToken && this.currentUser);
  }

  /**
   * Resolves all workspaces an authenticated user is authorized to access.
   */
  public getAuthorizedWorkspaces(user: AuthenticatedUser | null = this.currentUser): WorkspaceId[] {
    if (!user) return [];
    if (user.isSuperAdmin) {
      return ['SUPER_ADMIN', 'CASHIER', 'WAITER', 'MANAGER', 'STOCK_KEEPER', 'OWNER'];
    }
    const roleSet = new Set(user.roles.map((r) => r.role.toUpperCase()));
    const workspaces: WorkspaceId[] = [];

    // STRICT ROLE ISOLATION: Each account is completely private to its assigned role
    if (roleSet.has('OWNER')) {
      workspaces.push('OWNER');
    }
    if (roleSet.has('MANAGER')) {
      workspaces.push('MANAGER');
    }
    if (roleSet.has('CASHIER')) {
      workspaces.push('CASHIER');
    }
    if (roleSet.has('WAITER')) {
      workspaces.push('WAITER');
    }
    if (roleSet.has('INVENTORY_CLERK') || roleSet.has('STOCK_KEEPER')) {
      workspaces.push('STOCK_KEEPER');
    }
    return Array.from(new Set(workspaces));
  }

  public resolvePrimaryWorkspace(user: AuthenticatedUser): WorkspaceId {
    if (user.isSuperAdmin) return 'SUPER_ADMIN';
    const roles = user.roles.map((r) => r.role.toUpperCase());
    if (roles.includes('OWNER')) return 'OWNER';
    if (roles.includes('MANAGER')) return 'MANAGER';
    if (roles.includes('CASHIER')) return 'CASHIER';
    if (roles.includes('WAITER')) return 'WAITER';
    if (roles.includes('INVENTORY_CLERK') || roles.includes('STOCK_KEEPER')) return 'STOCK_KEEPER';
    return 'WAITER';
  }

  public getActiveWorkspace(): WorkspaceId | null {
    return this.activeWorkspace;
  }

  public setActiveWorkspace(workspace: WorkspaceId) {
    const allowed = this.getAuthorizedWorkspaces();
    if (!allowed.includes(workspace)) {
      throw new Error(`Unauthorized workspace selection: ${workspace}`);
    }
    this.activeWorkspace = workspace;
    this.persistSession();
  }

  public setSuperAdminTargetTenant(tenantId: string | null) {
    if (!this.currentUser?.isSuperAdmin) {
      throw new Error('Only ESS Super Admin can switch target tenant context');
    }
    this.superAdminTargetTenantId = tenantId;
    this.clearCache();
  }

  public getSuperAdminTargetTenant(): string | null {
    return this.superAdminTargetTenantId;
  }

  public getEffectiveTenantId(): string {
    if (this.currentUser?.isSuperAdmin && this.superAdminTargetTenantId) {
      return this.superAdminTargetTenantId;
    }
    return this.currentTenant?.id || 'platform-global';
  }

  private getCacheNamespace(): string {
    const uid = this.currentUser?.id || 'anon';
    const tid = this.getEffectiveTenantId();
    return `${uid}:${tid}`;
  }

  public clearCache(prefix?: string) {
    if (!prefix) {
      this.cache.clear();
      return;
    }
    const fullPrefix = `${this.getCacheNamespace()}:${prefix}`;
    for (const key of this.cache.keys()) {
      if (key.startsWith(fullPrefix)) {
        this.cache.delete(key);
      }
    }
  }

  public cancelRequest(cancelKey: string) {
    const controller = this.activeControllers.get(cancelKey);
    if (controller) {
      controller.abort();
      this.activeControllers.delete(cancelKey);
    }
  }

  private generateRequestId(): string {
    return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * Core HTTP transport with timeout, auth header, token refresh, and safe retry.
   */
  public async request<T>(
    path: string,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      body?: any;
      timeoutMs?: number;
      cancelKey?: string;
      cacheKey?: string;
      cacheTtlMs?: number;
      retryOnNetworkError?: boolean;
      skipAuthRefresh?: boolean;
    } = {}
  ): Promise<T> {
    const method = options.method || 'GET';
    const timeoutMs = options.timeoutMs ?? 12000;

    // 1. Check controlled tenant-isolated cache for GET requests
    if (method === 'GET' && options.cacheKey) {
      const namespacedKey = `${this.getCacheNamespace()}:${options.cacheKey}`;
      const hit = this.cache.get(namespacedKey);
      if (hit && hit.expiresAt > Date.now()) {
        return hit.data as T;
      }
    }

    // 2. Manage request cancellation if cancelKey supplied
    if (options.cancelKey) {
      this.cancelRequest(options.cancelKey);
    }
    const controller = new AbortController();
    if (options.cancelKey) {
      this.activeControllers.set(options.cancelKey, controller);
    }

    const timer = window.setTimeout(() => controller.abort(), timeoutMs);

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Request-ID': this.generateRequestId(),
    };

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    if (this.currentUser?.isSuperAdmin && this.superAdminTargetTenantId) {
      headers['x-tenant-id'] = this.superAdminTargetTenantId;
    }

    const url = path.startsWith('http') || path === '/health' ? path : `${this.baseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });

      const contentType = response.headers.get('content-type') || '';
      const json = contentType.includes('application/json') ? await response.json() : null;

      // Handle 401 Token Expiry with automatic refresh
      if (
        response.status === 401 &&
        !options.skipAuthRefresh &&
        this.refreshToken &&
        !path.includes('/auth/login') &&
        !path.includes('/auth/refresh')
      ) {
        const refreshed = await this.tryRefreshTokens();
        if (refreshed) {
          return this.request<T>(path, { ...options, skipAuthRefresh: true });
        } else {
          this.logout();
          if (this.onUnauthorizedCallback) this.onUnauthorizedCallback();
          throw new ApiError({
            message: 'Your session has expired. Please sign in again.',
            statusCode: 401,
            errorCode: 'SESSION_EXPIRED',
          });
        }
      }

      if (!response.ok) {
        throw new ApiError({
          message: json?.message || `HTTP Error ${response.status}`,
          statusCode: response.status,
          errorCode: json?.error || 'API_ERROR',
          details: json?.details,
          subscriptionStatus: json?.subscriptionStatus,
          renewalUrl: json?.renewalUrl,
        });
      }

      const payload = (json && 'data' in json ? json.data : json) as T;

      if (method === 'GET' && options.cacheKey) {
        const namespacedKey = `${this.getCacheNamespace()}:${options.cacheKey}`;
        this.cache.set(namespacedKey, {
          data: payload,
          expiresAt: Date.now() + (options.cacheTtlMs ?? 15000),
        });
      }

      return payload;
    } catch (err: any) {
      if (err instanceof ApiError) {
        throw err;
      }
      if (err?.name === 'AbortError') {
        throw new ApiError({
          message: 'Request timed out or was cancelled.',
          statusCode: 408,
          errorCode: 'REQUEST_TIMEOUT',
        });
      }
      // Safe retry ONLY for idempotent GET requests
      if (method === 'GET' && options.retryOnNetworkError) {
        return this.request<T>(path, { ...options, retryOnNetworkError: false });
      }
      throw new ApiError({
        message: err?.message || 'Unable to reach ESS backend server.',
        statusCode: 503,
        errorCode: 'NETWORK_ERROR',
      });
    } finally {
      window.clearTimeout(timer);
      if (options.cancelKey) {
        this.activeControllers.delete(options.cancelKey);
      }
    }
  }

  private async tryRefreshTokens(): Promise<boolean> {
    if (!this.refreshToken) return false;
    try {
      const tokens = await this.request<AuthTokens>('/auth/refresh', {
        method: 'POST',
        body: { refreshToken: this.refreshToken },
        skipAuthRefresh: true,
      });
      this.accessToken = tokens.accessToken;
      this.refreshToken = tokens.refreshToken;
      this.persistSession();
      return true;
    } catch {
      return false;
    }
  }

  // ==========================================================================
  // Authentication & Session Endpoints
  // ==========================================================================

  public async login(input: { email: string; password: string; tenantSlug?: string }): Promise<LoginResponseData> {
    this.clearCache();
    const data = await this.request<LoginResponseData>('/auth/login', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
    });
    this.accessToken = data.tokens.accessToken;
    this.refreshToken = data.tokens.refreshToken;
    this.currentUser = data.user;
    this.currentTenant = data.tenant;
    this.activeWorkspace = this.resolvePrimaryWorkspace(data.user);
    this.superAdminTargetTenantId = null;
    this.persistSession();
    return data;
  }

  public async stationPair(input: { branchCode: string; stationPin?: string }): Promise<{
    tenant: TenantSummary;
    branch: BranchRecord;
    staff: any[];
  }> {
    return this.request('/auth/station/pair', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
    });
  }

  public async stationStaffList(tenantId: string, branchId?: string): Promise<any[]> {
    return this.request('/auth/station/staff', {
      method: 'POST',
      body: { tenantId, branchId },
      skipAuthRefresh: true,
    });
  }

  public async staffPinLogin(input: { tenantId: string; userId: string; pin: string }): Promise<LoginResponseData> {
    this.clearCache();
    const data = await this.request<LoginResponseData>('/auth/station/pin-login', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
    });
    this.accessToken = data.tokens.accessToken;
    this.refreshToken = data.tokens.refreshToken;
    this.currentUser = data.user;
    this.currentTenant = data.tenant;
    this.activeWorkspace = this.resolvePrimaryWorkspace(data.user);
    this.superAdminTargetTenantId = null;
    this.persistSession();
    return data;
  }

  public async updateStaffPin(pin: string): Promise<any> {
    return this.request('/auth/station/update-pin', {
      method: 'POST',
      body: { pin },
    });
  }

  public async registerTenant(input: {
    businessName: string;
    businessSlug: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    password: string;
    planCode: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  }) {
    this.clearCache();
    const data = await this.request<any>('/auth/register', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
    });
    this.accessToken = data.tokens.accessToken;
    this.refreshToken = data.tokens.refreshToken;
    this.currentUser = data.user;
    this.currentTenant = data.tenant;
    this.activeWorkspace = 'OWNER';
    this.persistSession();
    return data;
  }

  public async seedDevelopmentDemo(): Promise<{ seeded: boolean; tenantSlug?: string }> {
    return this.request('/auth/dev-seed', {
      method: 'POST',
      skipAuthRefresh: true,
    });
  }

  public logout() {
    this.accessToken = null;
    this.refreshToken = null;
    this.currentUser = null;
    this.currentTenant = null;
    this.activeWorkspace = null;
    this.superAdminTargetTenantId = null;
    this.clearCache();
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }

  // ==========================================================================
  // Tenant, Branches, Stock Locations & Staff Endpoints
  // ==========================================================================

  public async getTenantProfile() {
    return this.request<{
      tenant: TenantSummary;
      subscription: any;
      metrics: { branches: number; users: number };
    }>('/tenants/profile', { cacheKey: 'tenant_profile', cacheTtlMs: 10000 });
  }

  public async listBranches(): Promise<BranchRecord[]> {
    return this.request<BranchRecord[]>('/tenants/branches', {
      cacheKey: 'branches',
      cacheTtlMs: 20000,
    });
  }

  public async createBranch(input: { name: string; code: string; address?: string; phone?: string }) {
    const res = await this.request<{ branch: BranchRecord; defaultStockLocation: StockLocationRecord }>(
      '/tenants/branches',
      { method: 'POST', body: input }
    );
    this.clearCache('branches');
    this.clearCache('stock_locations');
    this.clearCache('tenant_profile');
    return res;
  }

  public async listStockLocations(branchId?: string): Promise<StockLocationRecord[]> {
    const qs = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
    return this.request<StockLocationRecord[]>(`/tenants/stock-locations${qs}`, {
      cacheKey: `stock_locations_${branchId || 'all'}`,
      cacheTtlMs: 20000,
    });
  }

  public async createStockLocation(input: {
    branchId: string;
    name: string;
    type: 'RETAIL' | 'WAREHOUSE' | 'DAMAGED';
  }): Promise<StockLocationRecord> {
    const res = await this.request<StockLocationRecord>('/tenants/stock-locations', {
      method: 'POST',
      body: input,
    });
    this.clearCache('stock_locations');
    return res;
  }

  public async listStaff(): Promise<any[]> {
    return this.request<any[]>('/tenants/staff', {
      cacheKey: 'staff_list',
      cacheTtlMs: 10000,
    });
  }

  public async addStaffMember(input: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    password: string;
    role: 'MANAGER' | 'CASHIER' | 'INVENTORY_CLERK' | 'WAITER';
    branchId?: string;
    pin?: string;
  }) {
    const res = await this.request('/tenants/staff', {
      method: 'POST',
      body: input,
    });
    this.clearCache('staff_list');
    this.clearCache('tenant_profile');
    return res;
  }

  // ==========================================================================
  // Catalog (Categories & Products) Endpoints
  // ==========================================================================

  public async listCategories(): Promise<CategoryRecord[]> {
    return this.request<CategoryRecord[]>('/catalog/categories', {
      cacheKey: 'categories',
      cacheTtlMs: 15000,
    });
  }

  public async createCategory(name: string): Promise<CategoryRecord> {
    const res = await this.request<CategoryRecord>('/catalog/categories', {
      method: 'POST',
      body: { name },
    });
    this.clearCache('categories');
    return res;
  }

  public async listProducts(filters?: {
    q?: string;
    barcode?: string;
    categoryId?: string;
    forceRefresh?: boolean;
  }): Promise<ProductRecord[]> {
    if (filters?.forceRefresh) {
      this.clearCache('products');
    }
    const params = new URLSearchParams();
    if (filters?.q) params.set('q', filters.q);
    if (filters?.barcode) params.set('barcode', filters.barcode);
    if (filters?.categoryId) params.set('categoryId', filters.categoryId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    // Keep product cache short (5s) so stock and price changes stay fresh
    return this.request<ProductRecord[]>(`/catalog/products${qs}`, {
      cacheKey: `products_${qs}`,
      cacheTtlMs: 5000,
    });
  }

  public async createProduct(input: {
    name: string;
    description?: string;
    categoryId?: string;
    variants: Array<{
      sku: string;
      barcode?: string;
      variantName: string;
      costPriceCents: number;
      retailPriceCents: number;
      initialStock?: number;
      stockLocationId?: string;
    }>;
  }) {
    const res = await this.request('/catalog/products', {
      method: 'POST',
      body: input,
    });
    this.clearCache('products');
    this.clearCache('inventory');
    return res;
  }

  // ==========================================================================
  // Inventory Endpoints
  // ==========================================================================

  public async getInventoryBalances(locationId?: string): Promise<InventoryBalanceRecord[]> {
    const qs = locationId ? `?locationId=${encodeURIComponent(locationId)}` : '';
    return this.request<InventoryBalanceRecord[]>(`/inventory/balances${qs}`, {
      cacheKey: `inventory_balances_${locationId || 'all'}`,
      cacheTtlMs: 5000,
    });
  }

  public async receiveStock(input: {
    stockLocationId: string;
    items: Array<{ variantId: string; quantity: number; costPriceCents?: number }>;
    supplierName?: string;
    notes?: string;
  }) {
    const res = await this.request('/inventory/receive', {
      method: 'POST',
      body: input,
    });
    this.clearCache('inventory');
    this.clearCache('products');
    return res;
  }

  public async transferStock(input: {
    fromLocationId: string;
    toLocationId: string;
    variantId: string;
    quantity: number;
    notes?: string;
  }) {
    const res = await this.request('/inventory/transfer', {
      method: 'POST',
      body: input,
    });
    this.clearCache('inventory');
    this.clearCache('products');
    return res;
  }

  public async adjustStock(input: {
    stockLocationId: string;
    variantId: string;
    newQuantity: number;
    reason: 'DAMAGE_WRITEOFF' | 'ADJUSTMENT';
    notes: string;
  }) {
    const res = await this.request('/inventory/adjust', {
      method: 'POST',
      body: input,
    });
    this.clearCache('inventory');
    this.clearCache('products');
    return res;
  }

  public async getInventoryMovements(variantId?: string): Promise<InventoryMovementRecord[]> {
    const qs = variantId ? `?variantId=${encodeURIComponent(variantId)}` : '';
    return this.request<InventoryMovementRecord[]>(`/inventory/movements${qs}`);
  }

  // ==========================================================================
  // Sales & Cashier Shifts Endpoints (NO Automatic Retries on Financial Mutations)
  // ==========================================================================

  public async getActiveShift(): Promise<CashierShiftRecord | null> {
    return this.request<CashierShiftRecord | null>('/sales/shifts/active');
  }

  public async openShift(input: { branchId: string; openingFloatCents: number }): Promise<CashierShiftRecord> {
    return this.request<CashierShiftRecord>('/sales/shifts/open', {
      method: 'POST',
      body: input,
      retryOnNetworkError: false,
    });
  }

  public async closeShift(
    shiftId: string,
    input: { actualCashCents: number; closingNotes?: string }
  ): Promise<{
    shift: CashierShiftRecord;
    summary: {
      opening_float_cents: number;
      cash_sales_cents: number;
      expected_cash_cents: number;
      actual_cash_cents: number;
      variance_cents: number;
      is_balanced: boolean;
    };
  }> {
    return this.request(`/sales/shifts/${encodeURIComponent(shiftId)}/close`, {
      method: 'POST',
      body: input,
      retryOnNetworkError: false,
    });
  }

  public async checkoutSale(input: {
    branchId: string;
    stockLocationId: string;
    shiftId?: string;
    customerId?: string;
    items: Array<{ variantId: string; quantity: number; discountCents?: number }>;
    paymentMethod: 'CASH' | 'MPESA' | 'CARD' | 'SPLIT';
    cashTenderedCents?: number;
    mpesaReference?: string;
    idempotencyKey: string;
  }): Promise<{
    sale: SaleRecord;
    items: any[];
    receipt: {
      receiptNumber: string;
      totalCents: number;
      paymentMethod: string;
      cashTenderedCents?: number;
      changeCents: number;
      createdAt: string;
    };
    isDuplicate: boolean;
  }> {
    const res = await this.request<any>('/sales/checkout', {
      method: 'POST',
      body: input,
      retryOnNetworkError: false, // Never blindly retry financial transactions
    });
    this.clearCache('products');
    this.clearCache('inventory');
    return res;
  }

  public async listSales(branchId?: string, limit = 50): Promise<SaleRecord[]> {
    const params = new URLSearchParams();
    if (branchId) params.set('branchId', branchId);
    params.set('limit', String(limit));
    return this.request<SaleRecord[]>(`/sales?${params.toString()}`);
  }

  // ==========================================================================
  // Billing, Subscriptions & Reports Endpoints
  // ==========================================================================

  public async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    return this.request<SubscriptionStatusResponse>('/billing/status');
  }

  public async listSubscriptionPlans(): Promise<any[]> {
    return this.request<any[]>('/billing/plans', { cacheKey: 'plans', cacheTtlMs: 60000 });
  }

  public async listInvoices(): Promise<any[]> {
    return this.request<any[]>('/billing/invoices');
  }

  public async initiateSubscriptionMpesaPayment(input: {
    invoiceId?: string;
    phoneNumber: string;
  }): Promise<{
    paymentAttemptId: string;
    checkoutId: string;
    amountCents: number;
    phoneNumber: string;
    message: string;
  }> {
    return this.request('/billing/subscriptions/pay', {
      method: 'POST',
      body: input,
      retryOnNetworkError: false,
    });
  }

  public async getDailySalesReport(branchId?: string, date?: string): Promise<DailySalesReport> {
    const params = new URLSearchParams();
    if (branchId) params.set('branchId', branchId);
    if (date) params.set('date', date);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<DailySalesReport>(`/reports/daily-sales${qs}`);
  }

  public async getInventoryValuationReport(branchId?: string): Promise<InventoryValuationReport> {
    const qs = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
    return this.request<InventoryValuationReport>(`/reports/inventory-valuation${qs}`);
  }

  // ==========================================================================
  // Platform-Level ESS Super Admin Endpoints
  // ==========================================================================

  public async getPlatformOverview(): Promise<{
    tenants: any[];
    plans: any[];
    metrics: {
      totalTenants: number;
      activeTenants: number;
      lockedTenants: number;
      mrrCents: number;
      outboxPending: number;
    };
  }> {
    return this.request('/tenants/platform/overview');
  }

  public async updatePlatformTenantStatus(
    tenantId: string,
    status: 'ACTIVE' | 'GRACE_PERIOD' | 'LOCKED_UNPAID' | 'SUSPENDED'
  ) {
    return this.request(`/tenants/platform/${encodeURIComponent(tenantId)}/status`, {
      method: 'PATCH',
      body: { status },
    });
  }

  public async superAdminOnboardTenant(input: {
    businessName: string;
    businessSlug: string;
    planCode: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
    currency?: string;
    role?: 'CASHIER' | 'WAITER' | 'MANAGER' | 'INVENTORY_CLERK' | 'OWNER';
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    password: string;
    pin?: string;
    branchName?: string;
    branchCode?: string;
    branchAddress?: string;
    branchPhone?: string;
    easyMenuSlug?: string;
  }) {
    return this.request<any>('/tenants/platform/tenants', {
      method: 'POST',
      body: input,
    });
  }

  public async superAdminCreateBranch(
    tenantId: string,
    input: { name: string; code: string; address?: string; phone?: string }
  ) {
    return this.request<any>(`/tenants/platform/tenants/${encodeURIComponent(tenantId)}/branches`, {
      method: 'POST',
      body: input,
    });
  }

  public async superAdminCreateStaff(
    tenantId: string,
    input: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      password: string;
      pin?: string;
      role: 'MANAGER' | 'CASHIER' | 'INVENTORY_CLERK' | 'WAITER' | 'OWNER';
      branchId?: string;
    }
  ) {
    return this.request<any>(`/tenants/platform/tenants/${encodeURIComponent(tenantId)}/staff`, {
      method: 'POST',
      body: input,
    });
  }

  public async superAdminGrantTokens(tenantId: string, tokens: number) {
    return this.request<any>(`/tenants/platform/tenants/${encodeURIComponent(tenantId)}/tokens`, {
      method: 'POST',
      body: { tokens },
    });
  }

  public async superAdminDeleteTenant(tenantId: string) {
    return this.request<any>(`/tenants/platform/tenants/${encodeURIComponent(tenantId)}`, {
      method: 'DELETE',
    });
  }

  public async getSystemHealth(): Promise<{
    status: string;
    timestamp: string;
    uptimeSeconds: number;
    system: string;
    version: string;
  }> {
    return this.request('/health');
  }

  public async reconcilePayments(): Promise<any[]> {
    return this.request('/payments/reconcile', { method: 'POST', retryOnNetworkError: false });
  }

  // ==========================================================================
  // Restaurant Operations Endpoints
  // ==========================================================================

  public async listTables(): Promise<any[]> {
    return this.request<any[]>('/restaurant/tables');
  }

  public async createTable(data: { tableNumber: string; capacity: number; floorPlanZone?: string }): Promise<any> {
    return this.request<any>('/restaurant/tables', { method: 'POST', body: data });
  }

  public async updateTableStatus(tableId: string, status: string, assignedWaiterId?: string): Promise<any> {
    return this.request<any>(`/restaurant/tables/${encodeURIComponent(tableId)}/status`, {
      method: 'PATCH',
      body: { status, assignedWaiterId },
    });
  }

  public async listKitchenOrders(status?: string): Promise<any[]> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<any[]>(`/restaurant/orders${qs}`);
  }

  public async createKitchenOrder(data: {
    tableId?: string;
    shiftId?: string;
    notes?: string;
    items: Array<{ productVariantId: string; productName: string; quantity: number; notes?: string }>;
  }): Promise<any> {
    return this.request<any>('/restaurant/orders', { method: 'POST', body: data });
  }

  public async updateOrderStatus(orderId: string, status: string): Promise<any> {
    return this.request<any>(`/restaurant/orders/${encodeURIComponent(orderId)}/status`, {
      method: 'PATCH',
      body: { status },
    });
  }

  public async listRecipes(): Promise<any[]> {
    return this.request<any[]>('/restaurant/recipes');
  }

  public async createRecipe(data: any): Promise<any> {
    return this.request<any>('/restaurant/recipes', { method: 'POST', body: data });
  }

  public async listSuppliers(): Promise<any[]> {
    return this.request<any[]>('/restaurant/suppliers');
  }

  public async createSupplier(data: any): Promise<any> {
    return this.request<any>('/restaurant/suppliers', { method: 'POST', body: data });
  }

  public async listApprovals(status?: string): Promise<any[]> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<any[]>(`/restaurant/approvals${qs}`);
  }

  public async requestApproval(data: {
    requestType: 'DISCOUNT' | 'VOID' | 'REFUND' | 'PRICE_OVERRIDE';
    referenceId?: string;
    reason: string;
    amountCents?: number;
  }): Promise<any> {
    return this.request<any>('/restaurant/approvals/request', { method: 'POST', body: data });
  }

  public async resolveApproval(approvalId: string, status: 'APPROVED' | 'REJECTED', note?: string): Promise<any> {
    return this.request<any>(`/restaurant/approvals/${encodeURIComponent(approvalId)}/resolve`, {
      method: 'POST',
      body: { status, note },
    });
  }

  // ==========================================================================
  // Receipts, eTIMS Fiscal Invoices & Digital Email Dispatch
  // ==========================================================================

  public async getSaleReceipt(saleId: string): Promise<any> {
    return this.request<any>(`/sales/${encodeURIComponent(saleId)}/receipt`);
  }

  public async sendReceiptEmail(saleId: string, email: string): Promise<any> {
    return this.request<any>(`/sales/${encodeURIComponent(saleId)}/email-receipt`, {
      method: 'POST',
      body: { email },
    });
  }

  public async getEtimsInvoice(saleId: string): Promise<any> {
    return this.request<any>(`/sales/${encodeURIComponent(saleId)}/etims`);
  }

  public async sendShiftSummaryEmail(shiftId: string, email: string): Promise<any> {
    return this.request<any>(`/sales/shifts/${encodeURIComponent(shiftId)}/email-summary`, {
      method: 'POST',
      body: { email },
    });
  }

  // ==========================================================================
  // EasyMenu Bridge & Photography Sync
  // ==========================================================================

  public async importEasyMenu(slug: string, easyMenuApiUrl?: string): Promise<any> {
    return this.request<any>('/catalog/import-easymenu', {
      method: 'POST',
      body: { slug, easyMenuApiUrl },
    });
  }

  // ==========================================================================
  // QR Order Processing & Cashier Confirmation Queue
  // ==========================================================================

  public async listIncomingQrOrders(status?: string, branchId?: string): Promise<QrOrderSubmissionRecord[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (branchId) params.set('branchId', branchId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<QrOrderSubmissionRecord[]>(`/qr-orders/incoming${qs}`);
  }

  public async getQrOrder(id: string): Promise<QrOrderSubmissionRecord> {
    return this.request<QrOrderSubmissionRecord>(`/qr-orders/${encodeURIComponent(id)}`);
  }

  public async acceptQrOrder(
    id: string,
    options?: { cashierShiftId?: string; notes?: string }
  ): Promise<any> {
    return this.request<any>(`/qr-orders/${encodeURIComponent(id)}/accept`, {
      method: 'POST',
      body: options || {},
    });
  }

  public async rejectQrOrder(id: string, reason: string): Promise<any> {
    return this.request<any>(`/qr-orders/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: { reason },
    });
  }

  public async submitQrOrder(data: any): Promise<any> {
    return this.request<any>('/qr-orders/submit', {
      method: 'POST',
      body: data,
    });
  }

  public async listActiveBills(branchId?: string): Promise<ActiveBillRecord[]> {
    const qs = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
    return this.request<ActiveBillRecord[]>(`/qr-orders/bills/active${qs}`);
  }
}

export const apiClient = new EssApiClient();

